import React, { useEffect } from "react";
import { Flex, Text } from "@fluentui/react-northstar";
import { useQuery } from "react-query";
import { Company, Scope } from "../models";
import ReportsService from "../service/ReportsService";
import { View360ReportTable } from "view360Card/components/view360ReportTable/View360ReportTable";
import pl from "shared/messages/pl";
import { SalesAndActivitySectionDataSource } from "./SalesAndActivitySection";
import FailedToFetchReportError from "../error/FailedToFetchReportError";

export interface SalesSubSectionProps {
    dataSource: SalesAndActivitySectionDataSource;
    id: number;
    salesPersonIntermediaryNumber?: string;
    scope: Scope | undefined;
    company: Company;
    selectedSegments: string[];
    selectedYears: string[];
    selectedMode: string;
}

export const SalesSubSection: React.FC<SalesSubSectionProps> = (props) => {
    const queryKey = `sales_${props.company.code}`;

    const { isFetching, isError, data, refetch } = useQuery(queryKey, async () => {
        switch (props.dataSource) {
            case SalesAndActivitySectionDataSource.salesPerson:
                return await ReportsService.getSalesPersonSalesReport(
                    props.id,
                    props.salesPersonIntermediaryNumber,
                    props.scope,
                    props.company.code,
                    props.selectedSegments,
                    props.selectedYears,
                    props.selectedMode
                );

            case SalesAndActivitySectionDataSource.intermediary:
                return await ReportsService.getIntermediarySalesReport(
                    props.id,
                    props.scope,
                    props.company.code,
                    props.selectedSegments,
                    props.selectedYears,
                    props.selectedMode
                );

            default:
                throw new Error("Sales report data source has not been implemented.");
        }
    });

    useEffect(() => {
        const init = async () => {
            await refetch();
        };

        init();
    }, [
        props.salesPersonIntermediaryNumber,
        props.scope?.code,
        props.selectedSegments.length,
        props.selectedYears.length,
        props.selectedMode,
    ]);

    if (isError) {
        return (
            <FailedToFetchReportError
                message={pl.view360Card.reports.errors.failedToFetchSalesReportErrorMessage(props.company.name)}
            />
        );
    }

    return (
        <Flex column gap="gap.small">
            <Text size="medium" weight="semibold">
                {pl.view360Card.reports.salesSubSectionTitle(props.company.name)}
            </Text>
            <View360ReportTable reportData={data} />
        </Flex>
    );
};
